import React from 'react';
import { PersonDataContext } from '../../contexts/person-data-context';
import {
  getDocumentFileNames,
  nextCheckpointDelay,
} from '../../utils/upload-activities';
import { log } from '../../utils/log';
import { useUploadActivities } from '../use-data';
import { useAvailableDocIds } from './available-doc-ids';
import { runUploadCheckpoint } from './run-checkpoint';

export { useAvailableDocIds } from './available-doc-ids';
export { awaitNewUpload } from './await-new-upload';

/**
 * Polls for recently uploaded documents at fixed checkpoints after each
 * upload, until each is available, has failed, or has aged out. Mount once,
 * above the routes, so polling carries on across pages.
 */
export const useUploadPolling = () => {
  const { personData, refreshPersonData } = React.useContext(PersonDataContext);
  const { data, refetch } = useUploadActivities();
  const availableDocIds = useAvailableDocIds();
  const [checkpointsRun, setCheckpointsRun] = React.useState(0);

  // read at checkpoint time so a checkpoint always sees the latest values
  const latest = React.useRef({ personData, refreshPersonData, refetch });
  latest.current = { personData, refreshPersonData, refetch };

  const activities = data?.activities;

  React.useEffect(() => {
    const delay = nextCheckpointDelay(activities ?? [], Date.now(), {
      availableDocIds,
      documentFileNames: getDocumentFileNames(latest.current.personData),
    });
    if (delay === undefined) {
      return undefined;
    }

    const timeout = setTimeout(async () => {
      try {
        await runUploadCheckpoint({
          refetchActivities: async () =>
            (await latest.current.refetch()).data?.activities ?? [],
          refreshPersonData: () => latest.current.refreshPersonData(),
          getDocumentFileNames: () =>
            getDocumentFileNames(latest.current.personData),
        });
      } catch (e) {
        log.error('Upload polling checkpoint failed', e);
      } finally {
        setCheckpointsRun((count) => count + 1);
      }
    }, delay);

    return () => clearTimeout(timeout);
  }, [activities, availableDocIds, checkpointsRun]);
};
